import React from 'react'

export const signUpSignInState = {
  nameFirst: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  nameLast: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  phone: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  email: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  password: {
    touched: false,
    value: '',
    error: false,
    active: false,
  },
  showOTPInput: false,
  otpChannel: 'phone',   // 'phone' | 'email' — which channel the OTP is sent to
  otp: Array(6).fill(''),
  otpActiveIdx: 0,
  accessTokens: [],
  selectedAccessToken: null,
  submitError: null,
  providerSignup: null,
  working: false
}

export const signUpSignInReducer = (state, action) => {
  switch (action.type) {
    case 'CHANGE_TEXT':
      return {
        ...state,
        [action.name]: {
          ...state[action.name],
          value: action.value,
          error: false
        }
      }
    case 'FOCUS':
      return {
        ...state,
        [action.name]: {
          ...state[action.name],
          touched: true,
          active: true,
        }
      }
    case 'BLUR':
      return {
        ...state,
        [action.name]: {
          ...state[action.name],
          active: false
        }
      }
    case 'ERROR':
      return {
        ...state,
        [action.name]: {
          ...state[action.name],
          error: true,
          active: false
        }
      }
    case 'RESET_OTP_INPUT':
      return {
        ...state,
        showOTPInput: !state.showOTPInput,
        otp: Array(6).fill(''),
        otpActiveIdx: 0,
        submitError: null
      }
    case 'TOGGLE_OTP_INPUT':
      return {
        ...state,
        showOTPInput: !state.showOTPInput,
      }
    case 'SET_OTP_CHANNEL':
      return {
        ...state,
        otpChannel: action.channel,
        // clear both identifiers + errors when switching channel so stale values
        // don't leak across the phone/email screens
        phone: { ...signUpSignInState.phone },
        email: { ...signUpSignInState.email },
        submitError: null,
        providerSignup: null
      }
    // recover from a create-account attempt whose email already belongs to an account: drop
    // back to the identifier-entry screen on the email channel with that address prefilled, so
    // the user can sign in with an emailed code instead of hitting the duplicate-email error.
    // Sets email.value directly (unlike SET_OTP_CHANNEL, which clears both identifiers) and
    // carries a friendly submitError explaining the switch.
    case 'REDIRECT_TO_EMAIL_SIGNIN':
      return {
        ...state,
        otpChannel: 'email',
        showOTPInput: false,
        providerSignup: null,
        phone: { ...signUpSignInState.phone },
        email: { ...signUpSignInState.email, value: action.email || '' },
        submitError: action.error ?? null
      }
    case 'BEGIN_PROVIDER_SIGNUP':
      return {
        ...state,
        providerSignup: {
          provider: action.provider,
          signupToken: action.signupToken,
          idToken: action.idToken,
          nonce: action.nonce,
        },
        nameFirst: { ...state.nameFirst, value: action.nameFirst || '' },
        nameLast: { ...state.nameLast, value: action.nameLast || '' },
        email: { ...state.email, value: action.email || '' },
        submitError: null
      }
    // abandon a provider signup and fall back to the OTP path. Deliberately leaves
    // submitError alone so the caller's explanation survives the transition back to
    // the entry screen.
    case 'END_PROVIDER_SIGNUP':
      return {
        ...state,
        providerSignup: null
      }
    case 'RESET_FORM':
      return {
        ...signUpSignInState
      }
    case 'SET_OTP':
      return {
        ...state,
        otp: action.otp,
        otpActiveIdx: action.otpActiveIdx
      }
    // clear the entered digits without toggling the screen or wiping submitError — used to
    // reset the boxes after a rejected code while keeping the user on the OTP screen
    case 'CLEAR_OTP':
      return {
        ...state,
        otp: Array(6).fill(''),
        otpActiveIdx: 0
      }
    case 'SET_OTP_ACTIVE_IDX':
      return {
        ...state,
        otpActiveIdx: action.otpActiveIdx
      }
    case 'SET_ACCESS_TOKENS':
      return {
        ...state,
        accessTokens: action.accessTokens
      }
    case 'SET_SELECTED_ACCESS_TOKEN':
      return {
        ...state,
        selectedAccessToken: action.token
      }
    case 'SET_SUBMIT_ERROR':
      return {
        ...state,
        submitError: action.error
      }
    case 'SET_WORKING':
      return {
        ...state,
        working: action.working
      }
    default:
      return state
  }
}

export const AuthContext = React.createContext()
export const useAuthContext = () => React.useContext(AuthContext)

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = React.useReducer(signUpSignInReducer, signUpSignInState)
  return (
    <AuthContext.Provider value={{ state, dispatch }}>
      {children}
    </AuthContext.Provider>
  )
}
